import { Add, Close, CopyAll, Delete, OpenInNew } from '@mui/icons-material';
import {
    Box,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { ICompanyApiKey } from '@sharedInterfaces/ICompanySettings';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import createNewApiKey from '@src/APIs/graphQl/Company/createNewApiKey';
import deleteApiKey from '@src/APIs/graphQl/Company/deleteApiKey';
import Button from '@src/App/Components/Buttons/Button/Button';
import { AskForTextDialog } from '@src/App/Components/Dialogs/AskForTextDialog/AskForTextDialog';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import RowElement from '@src/App/Components/General/FormularElements/RowElement/RowElement';
import type Company from '@src/Objects/Company';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface RestApiTabProps {
    apiKeys: ICompanyApiKey[];
    setCompany: (company: Company) => void;
}
export default function RestApiTab({ apiKeys, setCompany }: RestApiTabProps) {
    // apiKeys = [{ id: 1, note: 'test 123', createdAt: new Date(), createdBy: 1 }]
    const naviate = useNavigate();
    const showErrorDialog = useErrorDialog();
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    return (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {langStrings.restAPI}
                    </Typography>
                    <Divider />
                    <Button
                        text={langStrings.oftenUsed.documentation}
                        icon={<OpenInNew />}
                        onClick={() => naviate(`/${ELinks.DOCUMENTATION_REST}`)}
                    />
                </Box>
                <ApiKeyTable apiKeys={apiKeys} onAddApiKey={onAddApiKey} onDeleteApiKey={onDeleteApiKey} />
            </Box>
        </Paper>
    );

    function onAddApiKey() {
        openDialog(
            <AskForTextDialog
                id={'askForApiKeyDialog'}
                title={langStrings.addApiKey}
                message={langStrings.pleaseEnterApiKeyNote}
                defaultValue={''}
                resolve={async (note) => {
                    if (note === null) return;

                    createNewApiKey(note)
                        .then((result) => {
                            setCompany(result.company);

                            openDialog(
                                <ApiKeyDialog
                                    id="apiKey"
                                    title={langStrings.apiKey}
                                    apiKey={result.apiKey}
                                    resolve={() => {}}
                                />,
                            );
                        })
                        .catch((err) => {
                            showErrorDialog(err);
                        });
                }}
            />,
        );
    }

    function onDeleteApiKey(id: number) {
        deleteApiKey(id)
            .then(setCompany)
            .catch((err) => {
                showErrorDialog(err);
            });
    }
}

interface RestApiTableProps {
    apiKeys: ICompanyApiKey[];
    onAddApiKey: () => void;
    onDeleteApiKey: (id: number) => void;
}
function ApiKeyTable({ apiKeys, onAddApiKey, onDeleteApiKey }: RestApiTableProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);

    function getEmployeeName(userId: number) {
        const emp = allEmployees.find((e) => e.id === userId);
        return emp ? emp.title : 'Unknown';
    }

    const addButton = <AddApiKeyButton onAdd={onAddApiKey} />;
    return (
        <>
            {apiKeys?.length >= 7 && addButton}
            <TableContainer component={Paper}>
                <Table aria-label="apiKeyTable">
                    <TableHead>
                        <TableRow>
                            <TableCell>{langStrings.id}</TableCell>
                            <TableCell align="left">{langStrings.note}</TableCell>
                            <TableCell align="left">{langStrings.oftenUsed.createdBy}</TableCell>
                            <TableCell align="left">{langStrings.oftenUsed.createdAt}</TableCell>
                            <TableCell align="right">{langStrings.oftenUsed.delete}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {apiKeys && apiKeys.length > 0 ? (
                            apiKeys.map((key, index) => (
                                <TableRow
                                    key={key.id}
                                    sx={{
                                        backgroundColor: index % 2 === 1 ? 'rgba(0, 0, 0, 0.04)' : 'white',
                                    }}
                                >
                                    <TableCell>{key.id}</TableCell>
                                    <TableCell>{key.note}</TableCell>
                                    <TableCell>{getEmployeeName(key.createdBy)}</TableCell>
                                    <TableCell>{new Date(key.createdAt).toLocaleString()}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={onDeleteApiKey.bind(null, key.id)}>
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography variant="body1" color="textSecondary">
                                        {langStrings.noApiKeysYet}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {addButton}
        </>
    );
}

function AddApiKeyButton({ onAdd }: { onAdd: () => void }) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;

    return (
        <Button
            icon={<Add />}
            text={langStrings.addApiKey}
            style={{ maxWidth: 300, margin: '5px 0px' }}
            onClick={onAdd}
        />
    );
}

interface ApiKeyDialogProps {
    id: string;
    title: string;
    apiKey: string;
    resolve: () => void;
}

function ApiKeyDialog({ id, title, apiKey, resolve }: ApiKeyDialogProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;

    return (
        <Dialog
            id={id}
            title={title}
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <Button text={langStrings.oftenUsed.close} icon={<Close />} onClick={resolve} />
                </div>
            }
            onClose={resolve}
        >
            {/* {apiKey} */}

            <RowElement title={langStrings.apiKey}>
                <TextField
                    fullWidth
                    variant="outlined"
                    value={apiKey}
                    disabled
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <IconButton title={langStrings.oftenUsed.copy} onClick={copyToClipboard.bind(null, apiKey)}>
                                <CopyAll />
                            </IconButton>
                        ),
                    }}
                />
            </RowElement>
        </Dialog>
    );
}

function copyToClipboard(link: string) {
    navigator.clipboard.writeText(link);
}
